import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {UrlService} from './url.service';

@Injectable()
export class ReportsService {
    private readonly BASE_URL: string;

    constructor(
        private http: HttpClient,
        private urlService: UrlService
    ) {
        this.BASE_URL = this.urlService.getBackendUrl() + 'data/v2/reports';
    }

    getAssessmentComparisonData(assessmentIds: string[], assessmentGroupIds: string[],
                                includeAlgos: boolean, languageCode: string): Observable<any> {
        let params = new HttpParams();

        if (assessmentIds && assessmentIds.length > 0) {
            params = params.set('assessmentIds', assessmentIds.join(','));
        }

        if (assessmentGroupIds && assessmentGroupIds.length > 0) {
            params = params.set('assessmentGroupIds', assessmentGroupIds.join(','));
        }

        params = params.set('includeAlgos', includeAlgos ? 'true' : 'false');
        if (languageCode) {
            params = params.set('languageCode', languageCode);
        }

        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/assessmentComparison', requestOptions);
    }

    // Kentucky Assessment Report methods
    getKentuckyAssessmentComparisonData(assessmentGroupId: string, assessmentGroupComparisonIds: string[],
                                        includeAlgos: boolean, languageCode: string): Observable<any> {
        let params = new HttpParams();

        if (assessmentGroupId) {
            params = params.set('assessmentGroupId', assessmentGroupId);
        }
        if (assessmentGroupComparisonIds && assessmentGroupComparisonIds.length > 0) {
            params = params.set('assessmentGroupComparisonIds', assessmentGroupComparisonIds.join(','));
        }

        params = params.set('includeAlgos', includeAlgos ? 'true' : 'false');
        if (languageCode) {
            params = params.set('languageCode', languageCode);
        }

        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/kentuckyAssessmentComparison', requestOptions);
    }

    // Tickler Episode Report methods
    getTicklerEpisodePagingKeys(ticklerEpisodeParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/ticklerEpisodePagingKeys', ticklerEpisodeParameters, requestOptions);
    }

    getTicklerEpisodeData(pagingKey: string | null, ticklerEpisodeParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();

        if (pagingKey) {
            params = params.set('pagingKeyJson', pagingKey);
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/ticklerEpisode', ticklerEpisodeParameters, requestOptions);
    }

    getTicklerEpisodeCsv(ticklerEpisodeParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params,
            responseType: 'text' as 'json'
        };

        return this.http.post(this.BASE_URL + '/ticklerEpisodeCsv', ticklerEpisodeParameters, requestOptions);
    }

    // Tickler Track Report methods (new report sourced from TicklerTrack; side-by-side with Tickler Episode)
    getTicklerTrackPagingKeys(ticklerTrackParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/ticklerTrackPagingKeys', ticklerTrackParameters, requestOptions);
    }

    getTicklerTrackData(pagingKey: string | null, ticklerTrackParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();

        if (pagingKey) {
            params = params.set('pagingKeyJson', pagingKey);
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/ticklerTrack', ticklerTrackParameters, requestOptions);
    }

    getTicklerTrackCsv(ticklerTrackParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params,
            responseType: 'text' as 'json'
        };

        return this.http.post(this.BASE_URL + '/ticklerTrackCsv', ticklerTrackParameters, requestOptions);
    }

    getExpectedAssessmentsData(expectedAssessmentsParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/expectedAssessments', expectedAssessmentsParameters, requestOptions);
    }

    // Server-side paging: one page per OU (mirrors the Tickler Track report).
    getExpectedAssessmentsPagingKeys(expectedAssessmentsParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/expectedAssessmentsPagingKeys', expectedAssessmentsParameters, requestOptions);
    }

    getExpectedAssessmentsDataWithPagingKey(pagingKey: string | null, expectedAssessmentsParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (pagingKey) {
            params = params.set('pagingKeyJson', pagingKey);
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/expectedAssessments', expectedAssessmentsParameters, requestOptions);
    }

    getExpectedAssessmentsCsv(expectedAssessmentsParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params,
            responseType: 'text' as 'json'
        };

        return this.http.post(this.BASE_URL + '/expectedAssessmentsCsv', expectedAssessmentsParameters, requestOptions);
    }

    getExpectedAssessmentsDetailData(expectedAssessmentsDetailParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/expectedAssessmentsDetail', expectedAssessmentsDetailParameters, requestOptions);
    }

    getExpectedAssessmentsDetailCsv(expectedAssessmentsDetailParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params,
            responseType: 'text' as 'json'
        };

        return this.http.post(this.BASE_URL + '/expectedAssessmentsDetailCsv', expectedAssessmentsDetailParameters, requestOptions);
    }

    getDistinctInstrumentTypes(assessments: any[]): string[] {
        const types: string[] = [];
        assessments.forEach((assessment) => {
            if (types.indexOf(assessment.type) < 0) {
                types.push(assessment.type);
            }
        });
        return types;
    }

    getImpactOutcomesCsv(impactParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params,
            responseType: 'text' as 'json'
        };

        return this.http.post(this.BASE_URL + '/impactOutcomesCsv', impactParameters, requestOptions);
    }

    getImpactCsv(impactParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params,
            responseType: 'text' as 'json'
        };

        return this.http.post(this.BASE_URL + '/impactCsv', impactParameters, requestOptions);
    }

    getImpactData(impactParameters: any, currentPage?: number, pageSize?: number, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();

        if (currentPage !== undefined && currentPage !== null) {
            params = params.set('page', currentPage.toString());
        }
        if (pageSize !== undefined && pageSize !== null) {
            params = params.set('pageSize', pageSize.toString());
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/impact', impactParameters, requestOptions);
    }

    getImpactDetailPageCount(impactParameters: any, pageSize: number, reportDataId?: string | null): Observable<number> {
        let params = new HttpParams();

        if (pageSize) {
            params = params.set('resultsPerPage', pageSize.toString());
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params
        };

        return this.http.post<number>(this.BASE_URL + '/impactDetailPageCount', impactParameters, requestOptions);
    }

    // Impact Detail Report methods
    getImpactDetailData(impactParameters: any, currentPage?: number, pageSize?: number, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();

        if (currentPage !== undefined && currentPage !== null) {
            params = params.set('pageNumber', currentPage.toString());
        }
        if (pageSize !== undefined && pageSize !== null) {
            params = params.set('resultsPerPage', pageSize.toString());
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/impactDetailData', impactParameters, requestOptions);
    }

    // Impact Report methods
    getImpactDetailCsv(impactParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params,
            responseType: 'text' as 'json'
        };

        return this.http.post(this.BASE_URL + '/impactDetailCsv', impactParameters, requestOptions);
    }

    // Impact Outcomes Report methods
    getImpactOutcomesPagingKeys(impactParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/impactReportsPagingKeys', impactParameters, requestOptions);
    }

    getImpactOutcomesData(impactParameters: any, currentPage?: number, pageSize?: number, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/impactOutcomes', impactParameters, requestOptions);
    }

    getImpactOutcomesDataWithPagingKey(pagingKey: any, impactParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();

        if (pagingKey) {
            params = params.set('pagingKeyJson', JSON.stringify(pagingKey));
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/impactOutcomes', impactParameters, requestOptions);
    }

    getImpactOutcomesPageCount(impactParameters: any, pageSize: number, reportDataId?: string | null): Observable<number> {
        let params = new HttpParams();

        if (pageSize) {
            params = params.set('pageSize', pageSize.toString());
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params
        };

        return this.http.post<number>(this.BASE_URL + '/impactDetailPageCount', impactParameters, requestOptions);
    }

    getImpactPageCount(impactParameters: any, pageSize: number, reportDataId?: string | null): Observable<number> {
        let params = new HttpParams();

        if (pageSize) {
            params = params.set('pageSize', pageSize.toString());
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params
        };

        return this.http.post<number>(this.BASE_URL + '/impactDetailPageCount', impactParameters, requestOptions);
    }

    // Heat Map Report methods
    getHeatMapData(pagingKey: any, heatMapParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();

        if (pagingKey) {
            params = params.set('pagingKeyJson', JSON.stringify(pagingKey));
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/heatMapData', heatMapParameters, requestOptions);
    }

    getHeatMapPagingKeys(heatMapParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }

        const requestOptions = {
            params: params
        };

        return this.http.post(this.BASE_URL + '/heatMapPagingKeys', heatMapParameters, requestOptions);
    }

    // Approval Aggregate Report methods
    getApprovalAggregatePagingKeys(approvalParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params
        };
        return this.http.post(this.BASE_URL + '/approvalAggregatePagingKeys', approvalParameters, requestOptions);
    }

    getApprovalAggregateData(approvalParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params
        };
        return this.http.post(this.BASE_URL + '/approvalAggregate', approvalParameters, requestOptions);
    }

    getApprovalAggregateDataWithPagingKey(pagingKey: any, approvalParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (pagingKey) {
            params = params.set('pagingKeyJson', JSON.stringify(pagingKey));
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params
        };
        return this.http.post(this.BASE_URL + '/approvalAggregate', approvalParameters, requestOptions);
    }

    getApprovalAggregateCsv(approvalParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params,
            responseType: 'text' as 'json'
        };
        return this.http.post(this.BASE_URL + '/approvalAggregateCsv', approvalParameters, requestOptions);
    }

    // Approval Detail Report methods
    getApprovalDetailPageCount(approvalParameters: any, pageSize: number, reportDataId?: string | null): Observable<number> {
        let params = new HttpParams();
        if (pageSize) {
            params = params.set('resultsPerPage', pageSize.toString());
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params
        };
        return this.http.post<number>(this.BASE_URL + '/approvalDetailPageCount', approvalParameters, requestOptions);
    }

    getApprovalDetailData(approvalParameters: any, currentPage?: number, pageSize?: number, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (currentPage !== undefined && currentPage !== null) {
            params = params.set('pageNumber', currentPage.toString());
        }
        if (pageSize !== undefined && pageSize !== null) {
            params = params.set('resultsPerPage', pageSize.toString());
        }
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params
        };
        return this.http.post(this.BASE_URL + '/approvalDetailData', approvalParameters, requestOptions);
    }

    getApprovalDetailCsv(approvalParameters: any, reportDataId?: string | null): Observable<any> {
        let params = new HttpParams();
        if (reportDataId) {
            params = params.set('reportDataId', reportDataId);
        }
        const requestOptions = {
            params: params,
            responseType: 'text' as 'json'
        };
        return this.http.post(this.BASE_URL + '/approvalDetailCsv', approvalParameters, requestOptions);
    }
}
